import { GridOptions, GridReadyEvent, RowSelectedEvent } from "@ag-grid-community/core";
import { Component, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ItemDetail, ItemDetailAddon, ItemDetailStatusEnum, JoDetail, JoHeader, PoDetail } from "app/model/entities/entity-model";
import { ItemDetailStatusCodes } from "app/model/enums/item-detail-status-code";
import { JoStatusCode } from "app/model/enums/jo-status-code";

import { AgFns, GridOptionsExt } from "app/shared/ag-fns";
import { DialogBaseComponent } from "app/shared/dialog-base.component";
import { DomainBaseComponent } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import { FileViewDialogComponent, FileViewDialogParams, IDoc } from "app/shared/file-view-dialog.component";
import { NavFns } from "app/shared/nav-fns";
import { UnitOfWork } from "app/shared/unit-of-work";
import { ShippingService } from "app/shipping/shipping.service";
import _ from "lodash";
import { takeUntil } from "rxjs/operators";
import { InvoiceShipmentService } from "./invoice-shipment.service";
import { DateFns } from "@/shared/date-fns";
import { AgStyleFns } from "@/shared/ag-style-fns";
import { AddressDialogComponent } from "@/shared/address-dialog.component";
import { UniAgFns } from "@/shared/uni-ag-fns";
import { InvoiceDialogComponent } from "@/shared/invoice-dialog.component";
import { ItemDetailLogDialogComponent } from "@/shared/item-detail-log-dialog.component";

@Component({
  selector: "app-jo-in-process",
  templateUrl: "./jo-in-process.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class JoInProcessComponent extends DomainBaseComponent {
  selectedJoHeader: JoHeader;
  johGridOptions: GridOptionsExt;

  joDetails: JoDetail[];
  jodGridOptions: GridOptionsExt;

  itemDetails: ItemDetail[];
  itdGridOptions: GridOptionsExt;
  itemDetailId: string;

  invoicedItemDetails: ItemDetail[];
  invoicedItdGridOptions: GridOptionsExt;

  poDetails: PoDetail[];
  podGridOptions: GridOptionsExt;

  // invoiceHeader: InvoiceHeader;

  errorMessage = "";
  areAllFound = false;
  currentDate = new Date();
  isAccountAdmin = false;

  tabItd = { label: "WIP - Pulled or Received", id: "wipTab", ix: 0 };
  tabJod = { label: "J/O Details", id: "jodTab", ix: 1 };
  tabPod = { label: "P/O Details", id: "podTab", ix: 2 };
  tabInvoicedItd = {
    label: "Invoiced/Shipped",
    id: "invoicedItdTab",
    ix: 3,
  };

  constructor(
    public uow: UnitOfWork,
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    protected shippingService: ShippingService,
    protected invoiceShipmentService: InvoiceShipmentService,
  ) {
    super(domainService);

    this.route.paramMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  async updateFromContext() {
    this.johGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onJohGridReady,
      onRowSelected: this.onJohRowSelected,
      onModelUpdated: () => {
        this.clearCurrentSelection();
      },
      rowModelType: "serverSide",
      onPaginationChanged: (params) => {
        if (params.newPage) {
          this.clearCurrentSelection();
        }
      },
    });

    this.captureLocation(this.johGridOptions, "id");

    this.itdGridOptions = AgFns.initGridOptions(
      this,
      {
        onGridReady: this.onItdGridReady,
      },
      { detailProperty: "itemDetailAddons" },
    );
    this.updateItdMasterDetail(this.itdGridOptions);

    this.jodGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onJodGridReady,
    });

    this.invoicedItdGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onInvoicedItdGridReady,
    });

    this.podGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPodGridReady,
    });

    this.isAccountAdmin = this.domainService.authService.getUser().isAccountAdmin;

    this.isPageReady = true;
  }

  get canEditAddress() {
    return this.isAccountAdmin && this.selectedJoHeader?.joStatusId != JoStatusCode.Closed;
  }

  async updateDatasource() {
    const gridApi = this.johGridOptions && this.johGridOptions.apiX;
    if (gridApi == null) {
      return;
    }
    const ds = AgFns.buildDatasource(() => this.dbQueryService.createJoHeadersInProcessQuery());

    gridApi.setGridOption("serverSideDatasource", ds);
    await this.applyLocation();
  }

  async onJohRowSelected(e: RowSelectedEvent) {
    // check if a deselect event and ignore
    if (!e.node.isSelected()) {
      return;
    }

    const joh = e.data as JoHeader;
    if (!joh) {
      return;
    }
    this.selectedJoHeader = joh;

    this.replaceLocation(joh.id);

    return this.queryDetails(joh);
  }

  clearCurrentSelection() {
    // HACK:
    // this insures that we don't reenter here after the deselectAll call below which causes a modelUpdated event
    // which calls this.  The problem is when returning to the page this method is called before the 'key' row is selected
    // but then is called again after the row is selected ( because deselectAll causes modelUpdated after a timeout) and this
    // 2nd call clears the row that has just been selected. UGH.
    if (this.selectedJoHeader === null) {
      return;
    }
    const gridState = this.johGridOptions.context?.gridState;
    if (gridState.isNavigating) return;
    this.joDetails = [];
    this.poDetails = [];
    this.itemDetails = [];
    this.invoicedItemDetails = [];

    this.selectedJoHeader = null;
    this.johGridOptions.apiX?.deselectAll();

    // DO NOT do this here - because it it is called during page initialization onModelUpdated
    // and this will clear the row that has just been selected - by apply location.
    // this.replaceLocation();
  }

  async queryDetails(joh: JoHeader) {
    // run next two queries in parallel.
    const p1 = this.dbQueryService.getJoDetailsForJoHeader(joh.id).then((r) => {
      this.joDetails = r;
      this.poDetails = _.flatMap(r.map((jod) => jod.poDetails));
    });
    const p2 = this.dbQueryService.getItemDetailsByJoHeaderId(joh.id).then((r) => {
      // NOTE: invoiceHeader logic below shouldn't be necessary but 'bad' old data requires it.
      this.itemDetails = r.filter((itd) => itd.itemDetailStatusId < ItemDetailStatusEnum.Shipped && itd.itemDetailStatusId != 0);
      // (itd.itemDetailStatusId === ItemDetailStatusCode.InProcessPendingShipPrep
      //   || itd.itemDetailStatusId === ItemDetailStatusCode.InProcessPendingAddon
      // )
      // && itd.invoiceHeaderId == null);

      this.invoicedItemDetails = r.filter(
        (itd) =>
          itd.itemDetailStatusId === ItemDetailStatusEnum.Shipped ||
          itd.itemDetailStatusId == ItemDetailStatusEnum.InProcessVoucher ||
          itd.invoiceHeaderId != null,
      );
    });

    // need to wait for both queries to resolve so that breeze can link up invoiceDetails
    await Promise.all([p1, p2]);
  }

  async onJohGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        headerName: "Job Order Id",
        ...NavFns.createIdCellClickedNavProps("id", this.router, "/in-process/manage/job-orders"),
        filter: "agNumberColumnFilter",
        cellClass: "ag-left-aligned-cell",
        headerClass: "ag-left-aligned-cell",
        ...AgFns.fixWidth(140),
      },
      {
        headerName: "Account",
        field: "account.accountName",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(120),
      },
      {
        headerName: "J/O Date",
        field: "joDate",
        filter: "agDateColumnFilter",
        ...AgFns.fixWidth(140),
      },
      {
        headerName: "Ship By Date",
        field: "origExpectedShipDate",
        filter: "agDateColumnFilter",
        cellClass: (params) => AgStyleFns.getDueDateCellClass(params.value),
        ...AgFns.fixWidth(140),
      },
      {
        headerName: "Ship Frequency",
        field: "shipFrequency.name",
        filter: "agTextColumnFilter",
        ...AgFns.fixWidth(180),
      },
      {
        headerName: "Status",
        field: "joStatus.name",
        filter: "agTextColumnFilter",
        ...AgFns.fixWidth(250),
      },
      {
        ...AgFns.createCheckboxProps("Allow Partial Shipments", "allowPartialShipments"),
        editable: false,
        ...AgFns.fixWidth(160),
      },
    ];
    AgFns.initGrid(event, colDefs);
    this.updateDatasource();
  }

  onItdGridReady(event: GridReadyEvent) {
    const colDefs = [
      { ...UniAgFns.getManufacturerColDef("product.productType.manufacturer.name") },
      { ...UniAgFns.getProductTypeSKUColDef("product.productType.style") },
      { ...UniAgFns.getProductTypeDescriptionColDef("product.productType.description") },
      { ...UniAgFns.getFeatureChoicesColDef("product.featureChoicesExtract") },

      { headerName: "Status", field: "itemDetailStatus.name", ...AgFns.fixWidth(240) },
      {
        ...AgFns.createCheckboxProps("Account-Owned", "isAccountOwned"),
        editable: false,
        ...AgFns.fixWidth(120),
      },
      {
        ...AgFns.createButtonProps("", this.gotoProcess.bind(this), {
          label: "In Process",
          calcLabel: this.getProcessLabel.bind(this),
        }),
        colId: "inProcess",
        ...AgFns.fixWidth(120),
      },
      {
        headerName: "Identifier",
        field: "id",
        cellClass: AgStyleFns.getLinkCellClass() + " ag-left-aligned-cell",
        onCellClicked: (e) => this.openItemStory(e.data as ItemDetail),
        tooltipValueGetter: () => "Show this item's story",
        ...AgFns.fixWidth(160),
      },
    ];

    AgFns.initGrid(event, colDefs);
  }

  updateItdMasterDetail(parentGridOptions: GridOptions) {
    // rowtype is ItemDetailAddon
    const detailGridOptions = AgFns.createDetailGridOptions();
    detailGridOptions.columnDefs = [
      { headerName: "Addon", field: "addon.nameAndLocation" },
      { headerName: "Instructions", field: "addon.instructions" },
      { headerName: "Addl. Info", field: "additionalInfo" },
      {
        headerName: "AddonBin",
        field: "addonBinId",
        ...NavFns.createCellClickedCalcNavProps(this.router, (event) => {
          const itda = <ItemDetailAddon>event.data;
          return { navPath: `/in-process/manage/addon-bin/${itda.addonBinId}` };
        }),
      },
      // { headerName: 'Price/Unit', field: '???' },  // UnitPriceAmt is on JoDetailAddon - not ItemDetailAddon.
      // { ...AgFns.createCellButtonProps('Documents', this.viewDocCell)  },
    ];
    AgFns.updateColDefs(detailGridOptions.columnDefs);
    parentGridOptions.detailCellRendererParams = {
      detailGridOptions: detailGridOptions,
      getDetailRowData: (params) => {
        const itd = params.data as ItemDetail;
        params.successCallback(itd.itemDetailAddons);
      },
    };
  }

  onJodGridReady(event: GridReadyEvent) {
    const colDefs = [
      { ...UniAgFns.getManufacturerColDef("product.productType.manufacturer.name") },
      { ...UniAgFns.getProductTypeSKUColDef("product.productType.style") },
      { ...UniAgFns.getProductTypeDescriptionColDef("product.productType.description") },
      { ...UniAgFns.getFeatureChoicesColDef("product.featureChoicesExtract") },

      {
        headerName: "Exp. Ship",
        field: "calcExpectedShipDate",
        // valueGetter: (params) => {
        //   const jod = <JoDetail>params.data;
        //   return jod?.calcExpectedShipDate;
        // },
        valueFormatter: (params) => DateFns.fmtDate(params.value),
        cellClass: (params) => AgStyleFns.getDueDateCellClass(params.value),
        ...AgFns.fixWidth(140),
      },
      {
        headerName: "Quantities",
        children: [
          { headerName: "Order", field: "orderQty", ...AgFns.fixWidth(100) },
          { headerName: "Cancel", field: "cancelQty", ...AgFns.fixWidth(100) },
          // could break this into pulled vs rcvd
          {
            headerName: "Pull/Rcvd",
            colId: "idtQty",
            type: "numericColumn",
            headerTooltip: "(calc) Quantities already pulled or received",
            valueGetter: (params) => {
              const jod = <JoDetail>params.data;
              return jod.getItemDetailsRcvdQty();
            },
            ...AgFns.fixWidth(120),
          },
          {
            headerName: "Open P/O",
            colId: "poQty",
            type: "numericColumn",
            headerTooltip: "(calc) P/O Detail quantities not yet received or canceled",
            valueGetter: (params) => {
              const jod = <JoDetail>params.data;
              return jod.getPoDetailsRemainingQty();
            },
            ...AgFns.fixWidth(100),
          },
          {
            headerName: "Open",
            colId: "netQty",
            type: "numericColumn",
            headerTooltip:
              "(calc) The net quantities remaining after accounting for all pulled items and unreceived purchase order details",
            valueGetter: (params) => {
              const jod = <JoDetail>params.data;
              return jod.getUnresolvedQty();
            },
            ...AgFns.fixWidth(150),
          },
          // { headerName: 'Return Qty', field: 'returnQty', type: 'numericColumn',  },
          {
            headerName: "Voucher",
            colId: "voucherQty",
            type: "numericColumn",
            headerTooltip: "(calc) Item Details on voucher but not yet shipped for this JODetail",
            valueGetter: (params) => {
              const jod = <JoDetail>params.data;
              return jod.getItemDetailsVoucheredQty();
            },
            ...AgFns.fixWidth(120),
          },
          {
            headerName: "Ship",
            colId: "shippedQty",
            type: "numericColumn",
            headerTooltip: "(calc) Item Details shipped for this JODetail",
            valueGetter: (params) => {
              const jod = <JoDetail>params.data;
              return jod.getItemDetailsShippedQty();
            },
            ...AgFns.fixWidth(100),
          },
          {
            headerName: "Open",
            colId: "uncompleteQty",
            type: "numericColumn",
            headerTooltip: "(calc) Job Order quantities not yet shipped",
            valueGetter: (params) => {
              const jod = <JoDetail>params.data;
              return jod.getUnshippedQty();
            },
            ...AgFns.fixWidth(100),
          },
        ],
      },
      // { headerName: "J/O Detail Id", field: "id" },
    ];
    const sortFields = ["id"];
    const sortModel = sortFields.map((sf) => {
      return { colId: sf, sort: "asc" as const };
    });

    this.jodGridOptions.getRowClass = function (params) {
      const jod = params.node.data as JoDetail;
      if (jod.getUnshippedQty() == 0) {
        return "rs-completed";
      }
    };

    AgFns.initGrid(event, colDefs, sortModel);
  }

  onInvoicedItdGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        headerName: "Identifier",
        field: "id",
        cellClass: AgStyleFns.getLinkCellClass() + " ag-left-aligned-cell",
        onCellClicked: (e) => this.openItemStory(e.data as ItemDetail),
        tooltipValueGetter: () => "Show this item's story",
        ...AgFns.fixWidth(160),
      },

      { ...UniAgFns.getManufacturerColDef("product.productType.manufacturer.name") },
      { ...UniAgFns.getProductTypeDescriptionColDef("product.productType.description") },
      { ...UniAgFns.getProductTypeSKUColDef("product.productType.style") },
      { ...UniAgFns.getFeatureChoicesColDef("product.featureChoicesExtract") },

      { headerName: "Status", field: "itemDetailStatus.name", ...AgFns.fixWidth(250) },
      {
        ...AgFns.createCheckboxProps("Account-Owned", "isAccountOwned"),
        editable: false,
        ...AgFns.fixWidth(120),
      },
      {
        ...AgFns.createButtonProps("Voucher", this.openInvoiceDialog.bind(this), {
          calcLabel: (itd: ItemDetail) => itd.invoiceHeaderId,
        }),
        field: "invoiceHeaderId",
        ...AgFns.fixWidth(120),
      },
    ];
    AgFns.initGrid(event, colDefs);
  }

  onPodGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        headerName: "Purchase Order",
        ...NavFns.createIdCellClickedNavProps("poHeaderId", this.router, "/purchasing/manage/purchase-orders"),
        ...AgFns.fixWidth(120),
      },

      { ...UniAgFns.getManufacturerColDef("product.productType.manufacturer.name") },
      { ...UniAgFns.getProductTypeSKUColDef("product.productType.style") },
      { ...UniAgFns.getProductTypeDescriptionColDef("product.productType.description") },
      { ...UniAgFns.getFeatureChoicesColDef("product.featureChoicesExtract") },

      {
        headerName: "Status",
        field: "poHeader.poStatus.name",
        cellStyle: (params) => {
          if (params.data.poHeader.poStatusId == "1000.00") {
            return { "background-color": "#ffcccc" };
          }
        },
        ...AgFns.flexWidth(250),
      },
      {
        headerName: "Dates",
        children: [
          {
            headerName: "Exp. Receive",
            field: "expectedReceiveDate",
            valueGetter: (params) => {
              const pending = params.data.getRemainingQty();
              if (pending > 0) {
                return params.data.expectedReceiveDate;
              } else {
                return null;
              }
            },
            cellClass: (params) => {
              const pod = params.data as PoDetail;
              return AgStyleFns.getPodDueDateCellClass(pod.poHeader.isProcessed(), pod.getRemainingQty() > 0, params.value);
            },
            // ...AgFns.fixWidth("date"),
            ...AgFns.fixWidth(140),
          },
          {
            headerName: "Exp. Ship",
            field: "expectedShipDate",
            valueGetter: (params) => {
              const pod = <PoDetail>params.data;
              const pending = pod?.getRemainingQty();
              if (pending > 0) {
                return params.data.expectedShipDate;
              } else {
                return null;
              }
            },
            valueFormatter: (params) => DateFns.fmtDate(params.value),
            cellClass: (params) => {
              const pod = params.data as PoDetail;
              return AgStyleFns.getPodDueDateCellClass(pod.poHeader.isProcessed(), pod.getRemainingQty() > 0, params.value);
            },
            ...AgFns.fixWidth(120),
          },
        ],
      },
      {
        headerName: "Quantities",
        children: [
          {
            headerName: "Order",
            field: "orderQty",
            ...AgFns.fixWidth(100),
            type: "numericColumn",
          },
          {
            headerName: "Rcvd",
            type: "numericColumn",
            ...AgFns.fixWidth(100),
            valueGetter: (params) => {
              const pod = <PoDetail>params.data;
              return pod?.getReceivedQty();
            },
          },
          {
            headerName: "Dmgd",
            headerTooltip: "(calc) Of those received, how many are damaged, lost or other",
            type: "numericColumn",
            ...AgFns.fixWidth(100),
            valueGetter: (params) => {
              const pod = <PoDetail>params.data;
              return pod?.getDamagedQty();
            },
          },
          {
            headerName: "Cncl",
            type: "numericColumn",
            ...AgFns.fixWidth(100),
            valueGetter: (params) => {
              const pod = <PoDetail>params.data;
              return pod?.getCanceledQty();
            },
          },
          {
            headerName: "Pend",
            type: "numericColumn",
            ...AgFns.fixWidth(100),
            valueGetter: (params) => {
              const pod = <PoDetail>params.data;
              return pod?.getRemainingQty();
            },
          },
          {
            headerName: "Ship",
            type: "numericColumn",
            ...AgFns.fixWidth(100),
            valueGetter: (params) => {
              const pod = <PoDetail>params.data;
              return pod?.getShippedQty();
            },
          },
        ],
      },
      // { headerName: "P/O Detail", field: "id" },
      // { headerName: "J/O Detail", field: "joDetailId" },
    ];
    const sortFields = ["product.productType.description", "product.productType.style", "product.choiceValue", "poDetailTypeId"];
    const sortModel = sortFields.map((sf) => {
      return { colId: sf, sort: "asc" as const };
    });

    this.podGridOptions.getRowClass = function (params) {
      const pod = params.node.data as PoDetail;
      if (pod.getRemainingQty() == 0) {
        return "rs-completed";
      }
    };

    AgFns.initGrid(event, colDefs, sortModel);
  }

  async onTabChanged(event: any) {
    const ix = event.index;

    // We need to do this because the grid is not rendered correctly when hidden
    // slices below is to work around buttons not rendering correctly in hidden tabs
    if (this.tabItd.ix === ix) {
      // AgFns.sizeColumnsToFit(this.itdGridOptions);
    }
    if (this.tabJod.ix === ix) {
      // AgFns.sizeColumnsToFit(this.jodGridOptions);
    } else if (this.tabPod.ix === ix) {
      // AgFns.sizeColumnsToFit(this.podGridOptions);
    } else if (this.tabInvoicedItd.ix === ix) {
      // AgFns.sizeColumnsToFit(this.invoicedItdGridOptions);
    }
  }

  gotoProcess(itd: ItemDetail) {
    if (ItemDetailStatusCodes.Addon.includes(itd.itemDetailStatusId)) {
      const binId = itd.itemDetailAddons[0]?.addonBinId;
      if (binId == null) {
        this.toastr.warning(`ItemDetail ${itd.id} is Pending Addon but has no AddonBin assigned. Contact support.`);
        return;
      }
      this.router.navigate(["/in-process/manage/addon-bin", binId]);
    } else if (ItemDetailStatusCodes.ShipPrep.includes(itd.itemDetailStatusId)) {
      const params = NavFns.buildNavIdParams(itd.joDetail.joHeaderId, "joDetail.joHeader.id");
      this.router.navigate(["/in-process/manage/scan-station"], params);
    } else if (itd.invoiceHeaderId != null) {
      this.openInvoiceDialog(itd);
    } else {
    }
  }

  async openInvoiceDialog(itd: ItemDetail) {
    await InvoiceDialogComponent.openDialog(this.domainService.pngDialogService, {
      invoiceId: itd.invoiceHeaderId,
    });
  }

  getProcessLabel(itd: ItemDetail) {
    if (ItemDetailStatusCodes.Addon.includes(itd.itemDetailStatusId)) {
      const binId = itd.itemDetailAddons[0]?.addonBinId;
      return binId != null ? "AddonBin-" + binId : "Pending Addon (unassigned)";
    } else if (ItemDetailStatusCodes.ShipPrep.includes(itd.itemDetailStatusId)) {
      return "Ship Prep";
    } else if (ItemDetailStatusEnum.InProcessVoucher == itd.itemDetailStatusId) {
      return "Invoice";
    } else {
      return "xxx";
    }
  }

  // TODO: check if this is even needed any longer.
  printTrackingBarcode() {}

  hasAddonDocs(itdAddon: ItemDetailAddon) {
    return itdAddon?.addon.addonDocMaps.length > 0;
  }

  async viewAddonDocs(itdAddon: ItemDetailAddon) {
    const data: FileViewDialogParams = {
      header: "View Addon Document",
      docs: itdAddon.addon.addonDocMaps.map((x) => x.addonDoc as IDoc),
    };
    await DialogBaseComponent.open(FileViewDialogComponent, this, data);
  }

  isJoOpenComplete() {
    return this.selectedJoHeader?.joStatusId == JoStatusCode.OpenProcessComplete;
  }

  hasItemDetails() {
    return this.itemDetails && this.itemDetails.length > 0;
  }

  async editAddress(prefix: string, addressTypeName: string) {
    const r = await DialogBaseComponent.open(AddressDialogComponent, this, {
      entity: this.selectedJoHeader,
      addressPrefix: prefix,
      addressTypeName: addressTypeName,
      editable: true,
    });
  }

  async openItemStory(itd: ItemDetail) {
    if (!itd) return;
    await DialogBaseComponent.open(ItemDetailLogDialogComponent, this, { itemDetail: itd });
  }

  goBack() {
    this.domainService.location.back();
  }
}
